import React from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import { InvoiceAgeGroup } from "../../api/generated";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

export interface FilterValues {
  arrowId?: number | null,
  invoiceNumber?: string | null,
  assignedUserEmail?: string | null;
  supplierName?: string | null;
  supplierSite?: string | null;
  currency?: string | null;
  queueName?: string | null;
  businessUnit?: string | null;
  invoiceAge?: InvoiceAgeGroup | null;
  invoiceStartDate?: string | null;
  invoiceEndDate?: string | null;
  glStartDate?:string | null;
  glEndDate?:string | null;
  invoiceAmountRange?: string | null;
}

interface InvoiceFilterPanelProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  onSearch: (filters: FilterValues) => void;
  onClear: () => void;
  dropdownOptions: {
    currency: string[];
    assignedTo: string[];
    queueName: string[];
    opcoName: string[];
  };
  showClearResultsButton: boolean;
  resetActiveFilter?: () => void;
  dateFilter?: any;
  lastFilterSource?: string;
}

const invoiceAges = ["ALL",'Received Today', '1-7 days', '8-15 days', '16+ days'];
const invoiceAmountRanges = ["≤ −10,000","-10,000 to -5,001","-5,000 to -1","0 to 5,000","5,001 to 10,000","≥ 10,000"];


export default function InvoiceFilterPanel({
  filters,
  onFilterChange,
  onSearch,
  onClear,
  dropdownOptions,
  showClearResultsButton,
}: InvoiceFilterPanelProps) {
  const handleChange = (field: keyof FilterValues, value: any) => {
    onFilterChange({
      ...filters,
      [field]: value,
    });
  };

  const [expanded, setExpanded] = React.useState(false);

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion sx={{
      width: '99%', backgroundColor: "var(--background-off-white)",
      boxShadow: 'none',
      border: 'none',
      '&:before': { display: 'none' },
      padding: 0,
      margin: 0,
    }}
    expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary
        aria-controls="filter-content"
        id="filter-header"
        sx={{ padding: '0' , marginTop:'20px', width: '100px'}}
      >
        <Box display="flex" alignItems="center">
          <Typography sx={{color: "var(--brand-default-color)", fontWeight: 600}}>Filters</Typography>
          {expanded ? <ExpandLessIcon sx={{ ml: 1, fill: "var(--brand-default-color)" }} /> : <ExpandMoreIcon sx={{ ml: 1, fill: "var(--brand-default-color)" }} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0' }}>
        <Box
          sx={{
            borderRadius: 2,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Arrow ID"
              value={filters.arrowId || ""}
              onChange={e => handleChange("arrowId", e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Invoice Number"
              value={filters.invoiceNumber || ""}
              onChange={e => handleChange("invoiceNumber", e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Business Unit</InputLabel>
              <Select
                 value={
                  dropdownOptions.opcoName.includes(filters.businessUnit)
                    ? filters.businessUnit
                    : ""
                }
                label="Business Unit"
                onChange={e => handleChange("businessUnit", e.target.value)}
              >
                {/* <MenuItem value="">All</MenuItem> */}
                {dropdownOptions.opcoName.map(bu => (
                  <MenuItem key={bu} value={bu}>
                    {bu}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Worklist</InputLabel>
              <Select
              value={
                dropdownOptions.queueName.includes(filters.queueName)
                  ? filters.queueName
                  : ""
              }
                label="Worklist"
                onChange={e => handleChange("queueName", e.target.value)}
              >
                {/* <MenuItem value="">All</MenuItem> */}
                {dropdownOptions.queueName.map(w => (
                  <MenuItem key={w} value={ w}>
                    {w}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Assigned to</InputLabel>
              <Select
                value={
                  dropdownOptions.assignedTo.includes(filters.assignedUserEmail)
                    ? filters.assignedUserEmail
                    : ""
                }
                label="Assigned to"
                onChange={e => handleChange("assignedUserEmail", e.target.value)}
              >
                {/* <MenuItem value="">All</MenuItem> */}
                {dropdownOptions.assignedTo.map(u => (
                  <MenuItem key={u} value={ u}>
                    {u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Invoice date Start date"
              type="date"
              value={filters.invoiceStartDate || ""}
              onChange={e => handleChange("invoiceStartDate", e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Invoice date End date"
              type="date"
              value={filters.invoiceEndDate || ""}
              onChange={e => handleChange("invoiceEndDate", e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="GL Start Date"
              type="date"
              value={filters.glStartDate || ""}
              onChange={e => handleChange("glStartDate", e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="GL End Date"
              type="date"
              value={filters.glEndDate || ""}
              onChange={e => handleChange("glEndDate", e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl sx={{ minWidth: 140 }} size="small">
              <InputLabel>Invoice age</InputLabel>
              <Select
                value={filters.invoiceAge || ""}
                label="Invoice age"
                onChange={e => handleChange("invoiceAge", e.target.value)}
              >
                {invoiceAges.map(age => (
                  <MenuItem key={age} value={age}>
                    {age}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Currency</InputLabel>
              <Select
                value={
                  dropdownOptions.currency.includes(filters.currency)
                    ? filters.currency
                    : ""
                }
                label="Currency"
                onChange={e => handleChange("currency", e.target.value)}
              >
                {/* <MenuItem value="ALL">All</MenuItem> */}
                {dropdownOptions.currency.map(c => (
                  <MenuItem key={c} value={ c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 160 }} size="small">
              <InputLabel>Invoice amount</InputLabel>
              <Select
                value={filters.invoiceAmountRange || ""}
                label="Invoice amount"
                onChange={e => handleChange("invoiceAmountRange", e.target.value)}
              >
                {invoiceAmountRanges.map(r => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Supplier name"
              value={filters.supplierName || ""}
              onChange={e => handleChange("supplierName", e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Supplier site"
              value={filters.supplierSite || ""}
              onChange={e => handleChange("supplierSite", e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                onClick={() => onSearch(filters)}
                variant="contained"
                startIcon={<SearchIcon sx={{ color: '#fff' }} />}
              >
                Apply filters
              </Button>
            </Box>
            {showClearResultsButton && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClear}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Box>

      </AccordionDetails>
    </Accordion >

  );
}
